"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { grantConsent } from "./Analytics";
import {
  SIGNUP_ATTRIBUTION_CONSENT_EVENT,
  formatClearSignupAttributionCookie,
} from "@/lib/signup-attribution";

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function emitConsentChange(consent: "accepted" | "rejected") {
  window.dispatchEvent(new CustomEvent(SIGNUP_ATTRIBUTION_CONSENT_EVENT, {
    detail: { consent },
  }));
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getCookie("otr_consent");
    if (!consent) {
      setVisible(true);
    } else if (consent === "accepted") {
      grantConsent({ sendPageView: false });
    }
  }, []);

  function handleAccept() {
    setCookie("otr_consent", "accepted", 365);
    setVisible(false);
    emitConsentChange("accepted");
    grantConsent();
  }

  function handleReject() {
    setCookie("otr_consent", "rejected", 365);
    document.cookie = formatClearSignupAttributionCookie({
      secure: window.location.protocol === "https:",
    });
    setVisible(false);
    emitConsentChange("rejected");
  }

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 bg-white border-t border-ink-200 shadow-lg px-6 py-4">
      <div className="mx-auto max-w-4xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-sm text-ink-600">
          We use cookies to understand how you use our site and improve your experience. See our{" "}
          <Link href="/legal/privacy" className="underline text-brand-600 hover:text-brand-700">
            privacy policy
          </Link>.
        </p>
        <div className="flex gap-3 shrink-0">
          <button
            onClick={handleReject}
            className="rounded-lg border border-ink-300 px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50 transition-colors"
          >
            Reject
          </button>
          <button
            onClick={handleAccept}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
